// =====================================================================================
// INVRT Radar — web console — API client binding + shared queries
//
// The client is rebuilt whenever the session or the chosen organisation changes, so every
// query key that includes `orgId` refetches on an org switch rather than showing another
// tenant's data for a frame.
// =====================================================================================

import { useMemo } from "react";
import { useQuery, type UseQueryResult } from "@tanstack/react-query";
import { ApiClient } from "@/lib/api";
import { useSession } from "@/auth/SessionProvider";
import type {
  BalanceResponse,
  CapabilitiesResponse,
  CreditPackList,
  PricingResponse,
  SiteList,
} from "@/lib/types";

export function useApiClient(): ApiClient {
  const { supabase, orgId } = useSession();

  return useMemo(
    () =>
      new ApiClient({
        // Resolved per request: supabase-js refreshes the access token in the background,
        // and reading it late means a long-lived page never sends a stale one.
        getAccessToken: async () => {
          const { data } = await supabase.auth.getSession();
          return data.session?.access_token ?? null;
        },
        orgId,
      }),
    [supabase, orgId],
  );
}

/**
 * The header balance. A SUM over credit_ledger computed in Postgres — this hook only ever
 * displays what it is handed.
 */
export function useBalance(): UseQueryResult<BalanceResponse> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["balance", orgId],
    queryFn: () => api.get<BalanceResponse>("/balance"),
    enabled: Boolean(orgId),
    staleTime: 15_000,
  });
}

export function usePricing(): UseQueryResult<PricingResponse> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["pricing", orgId],
    queryFn: () => api.get<PricingResponse>("/pricing"),
    enabled: Boolean(orgId),
    staleTime: 5 * 60_000,
  });
}

/** The purchasable PAYG credit-pack catalogue — only active, priced packs, free to read. */
export function usePacks(): UseQueryResult<CreditPackList> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["packs", orgId],
    queryFn: () => api.get<CreditPackList>("/packs"),
    enabled: Boolean(orgId),
    staleTime: 5 * 60_000,
  });
}

export function useCapabilities(): UseQueryResult<CapabilitiesResponse> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["capabilities", orgId],
    queryFn: () => api.get<CapabilitiesResponse>("/capabilities"),
    enabled: Boolean(orgId),
    staleTime: 60_000,
  });
}

export function useSites(): UseQueryResult<SiteList> {
  const api = useApiClient();
  const { orgId } = useSession();

  return useQuery({
    queryKey: ["sites", orgId],
    queryFn: () => api.get<SiteList>("/sites"),
    enabled: Boolean(orgId),
  });
}
